import React, { useRef } from "react";
import styled from "styled-components";
import { motion, useScroll } from "framer-motion";

import ExperienceCard from "../Cards/ExperienceCard";
import PointList from "../common/PointList";
import TrailMap from "./TrailMap";
import { experiences, activities } from "../../data/constants";
import {
  fadeUp,
  revealLine,
  slideFrom,
  staggerContainer,
  viewport,
} from "../../utils/motion";
import {
  SectionWrap,
  SectionInner,
  SectionHead,
  Eyebrow,
  SectionTitle,
  SectionDesc,
  MaskLine,
  RuleLabel,
} from "../common/Section";

// Experience / "Journey So Far". dell-1996's left-rail + right-column split
// (DESIGN.md:431-434): the sticky trail map holds the rail, the campsite
// dossiers scroll in the main column.

// align-items must NOT be `start` here: that collapses the map's grid cell to
// its own height, leaving `position: sticky` no room to travel, so the map
// scrolls away while the cards keep going. Stretch gives it the full row.
const Split = styled.div`
  display: grid;
  grid-template-columns: 0.72fr minmax(0, 1fr);
  gap: 32px;
  align-items: stretch;

  @media (max-width: 1024px) {
    grid-template-columns: minmax(0, 1fr);
    gap: 0;
  }
`;

// min-width: 0 lets the track shrink below its content's intrinsic width —
// without it a long window label (a company name + ".log") widens the whole
// grid past the viewport.
const Column = styled(motion.div)`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const RailLabel = styled.div`
  font-family: ${({ theme }) => theme.fonts.ui};
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 2.4px;
  text-transform: uppercase;
  color: ${({ theme }) => theme.biome.experience.muted};
  margin-bottom: 4px;
`;

const Experience = () => {
  const columnRef = useRef(null);

  // Progress through the stack of experience cards. The map's plane is flown
  // from this, so reaching the last card puts it at the last destination —
  // one destination per experience, no leftover route.
  // "end end" (not "end center") is what closes the gap: progress reaches 1
  // when the bottom of the last card reaches the bottom of the viewport, so
  // the plane lands on the final destination exactly as the last experience
  // finishes. Ending at "center" left roughly a fifth of the route unflown.
  const { scrollYProgress } = useScroll({
    target: columnRef,
    offset: ["start center", "end end"],
  });

  return (
  <SectionWrap id="experience" tone="experience">
    <SectionInner>
      <SectionHead
        variants={staggerContainer(0.08)}
        initial="hidden"
        whileInView="show"
        viewport={viewport}
      >
        <Eyebrow tone="experience" variants={fadeUp}>
          Journey so far
        </Eyebrow>
        <MaskLine>
          <SectionTitle tone="experience" variants={revealLine}>
            Experience
          </SectionTitle>
        </MaskLine>
        <SectionDesc tone="experience" variants={fadeUp}>
          Every stop, its story. Every role, its relic.
        </SectionDesc>
      </SectionHead>

      <Split>
        <TrailMap progress={scrollYProgress} />

        <Column
          ref={columnRef}
          variants={staggerContainer(0.1)}
          initial="hidden"
          whileInView="show"
          viewport={viewport}
        >
          <RailLabel>Campsites along the trail</RailLabel>
          {/* Dossiers slide in from the trail's side, so the map and the cards
              read as one movement rather than two. */}
          {experiences.map((experience) => (
            <motion.div
              key={experience.id}
              variants={slideFrom("right", 44)}
              initial="hidden"
              whileInView="show"
              viewport={viewport}
            >
              <ExperienceCard experience={experience} />
            </motion.div>
          ))}
        </Column>
      </Split>

      <RuleLabel tone="experience">Proving Ground</RuleLabel>
      <motion.div
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={viewport}
      >
        <PointList points={activities} bare />
      </motion.div>
    </SectionInner>
  </SectionWrap>
  );
};

export default Experience;
